/**
 * useDialogParam (KAN-710)
 *
 * The client conversation modal (ContactTranscriptDialog) lives in the URL so
 * a link to it can be shared:
 *   ?dialog=<clientId>            the client's conversation (main form);
 *   ?dialog=<clientId>&estimate=<estimateId>
 *                                 the same, manual messages go to that estimate;
 *   ?estimate=<estimateId>        a dialog without a client (old contacts).
 *
 * Opening pushes a history entry (Back closes the modal), closing removes the
 * params with replace. What the caller already knows (title, manual mode, our
 * account) rides in the history state as a hint, so a click opens the modal
 * at once; a shared link has no hint and TranscriptDialogHost loads it.
 */
import { useCallback, useMemo } from 'react'
import { useLocation, useSearchParams } from 'react-router-dom'
import type { SenderAccountLinkAccount } from '@/components/messages/SenderAccountLink'

export const DIALOG_PARAM = 'dialog'
export const DIALOG_ESTIMATE_PARAM = 'estimate'

/** Props the caller knows; a shared link has none of them. */
export interface DialogHint {
  title?: string
  manualMode?: boolean
  senderAccount?: SenderAccountLinkAccount | null
}

export interface DialogTarget {
  clientId?: string | null
  estimateId?: string | null
  hint?: DialogHint
}

interface DialogHistoryState {
  dialogHint?: DialogHint
}

function readHint(state: unknown): DialogHint | undefined {
  if (!state || typeof state !== 'object') return undefined
  return (state as DialogHistoryState).dialogHint
}

/** Params of a dialog link: dialog=<clientId> and/or estimate=<estimateId>. */
export function applyDialogParams(
  params: URLSearchParams,
  clientId: string | null | undefined,
  estimateId: string | null | undefined
): URLSearchParams {
  const next = new URLSearchParams(params)
  if (clientId) next.set(DIALOG_PARAM, clientId)
  else next.delete(DIALOG_PARAM)
  if (estimateId) next.set(DIALOG_ESTIMATE_PARAM, estimateId)
  else next.delete(DIALOG_ESTIMATE_PARAM)
  return next
}

/** The page address with the dialog params of the shown conversation. */
export function dialogShareUrl(
  href: string,
  clientId: string | null,
  estimateId: string | null
): string {
  const url = new URL(href)
  if (!clientId && !estimateId) return url.toString()
  url.search = applyDialogParams(url.searchParams, clientId, estimateId).toString()
  return url.toString()
}

export function useDialogParam() {
  const [searchParams, setSearchParams] = useSearchParams()
  const location = useLocation()
  const clientId = (searchParams.get(DIALOG_PARAM) ?? '').trim() || null
  const estimateId =
    (searchParams.get(DIALOG_ESTIMATE_PARAM) ?? '').trim() || null
  const hint = useMemo(() => readHint(location.state), [location.state])

  const openDialog = useCallback(
    ({ clientId: nextClient, estimateId: nextEstimate, hint: nextHint }: DialogTarget) => {
      if (!nextClient && !nextEstimate) return
      const state: DialogHistoryState = { dialogHint: nextHint }
      setSearchParams(
        (prev) => applyDialogParams(prev, nextClient, nextEstimate),
        { state }
      )
    },
    [setSearchParams]
  )

  const closeDialog = useCallback(() => {
    setSearchParams((prev) => applyDialogParams(prev, null, null), {
      replace: true,
    })
  }, [setSearchParams])

  return {
    clientId,
    estimateId,
    hint,
    isOpen: !!clientId || !!estimateId,
    openDialog,
    closeDialog,
  }
}
